import { situationCardSlotOrder } from "@peer-start/platform-contracts";
import { Badge, Skeleton } from "@peer-start/ui/reducate";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  localizeSlotText,
  parseNumberedSteps,
  visibleChanges,
  type SituationCardCopy,
  type SituationCardSection,
  type VisibleChange,
} from "../pages/situation-card-view.js";
import { SourceReferences } from "./SourceReference.js";

const order: readonly SituationCardSection[] = situationCardSlotOrder;

export interface SectionRevision {
  before: string;
  after: string;
}

function visibleParts(
  paragraphs: SituationCardCopy,
  revisions?: Partial<Record<SituationCardSection, SectionRevision>>,
) {
  return order
    .map((key) => ({ key, text: paragraphs[key].trim() }))
    .filter((part) => part.text || revisions?.[part.key]);
}

export function SituationCardSections({
  paragraphs,
  mode,
  revisions,
  references = {},
  onStreamDone,
}: {
  paragraphs: SituationCardCopy;
  mode: "steady" | "loading" | "streaming" | "settled";
  revisions?: Partial<Record<SituationCardSection, SectionRevision>>;
  references?: Partial<Record<SituationCardSection, number[]>>;
  onStreamDone?: () => void;
}) {
  const { t } = useTranslation();
  const titles = Object.fromEntries(
    order.map((key) => [key, t(`administration.situationCard.slots.${key}`)]),
  ) as Record<SituationCardSection, string>;
  const parts = visibleParts(paragraphs, revisions).map((part) => {
    const revision = revisions?.[part.key];
    return {
      key: part.key,
      text: localizeSlotText(part.key, part.text, t),
      revision: revision
        ? {
            before: localizeSlotText(part.key, revision.before, t),
            after: localizeSlotText(part.key, revision.after, t),
          }
        : undefined,
    };
  });
  if (mode === "loading") return <LoadingLines />;
  if (mode === "streaming") {
    const text = parts.map((part) => `${titles[part.key]}\n\n${part.text}`).join("\n\n");
    return <StreamingText onDone={onStreamDone} text={text} />;
  }
  return (
    <div className="space-y-6">
      {parts.map((part) => (
        <section key={part.key}>
          <h3 className="text-base font-medium">{titles[part.key]}</h3>
          <SectionBody
            references={references[part.key] ?? []}
            revision={part.revision}
            section={part.key}
            text={part.text}
          />
        </section>
      ))}
    </div>
  );
}

function SectionBody({
  section,
  text,
  revision,
  references,
}: {
  section: SituationCardSection;
  text: string;
  revision?: SectionRevision;
  references: readonly number[];
}) {
  if (revision) return <DiffText after={revision.after} before={revision.before} references={references} />;
  return section === "steps" ? <Steps body={text} references={references} /> : <Body references={references} text={text} />;
}

function Body({ text, references }: { text: string; references: readonly number[] }) {
  const blocks = text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
  return (
    <div className="mt-1 space-y-3">
      {blocks.map((block, index) => (
        <p className="text-sm leading-relaxed whitespace-pre-wrap text-secondary-text" key={index}>
          {block}
          <SourceReferences numbers={references} />
        </p>
      ))}
    </div>
  );
}

function Steps({ body, references }: { body: string; references: readonly number[] }) {
  const steps = parseNumberedSteps(body);
  if (!steps) return <Body references={references} text={body} />;
  return (
    <ol className="mt-2 space-y-4">
      {steps.map((step) => (
        <li key={step.number}>
          <p className="text-sm leading-relaxed text-secondary-text">
            {step.number}. {step.title}
            {step.body ? ` ${step.body}` : ""}
            <SourceReferences numbers={references} />
          </p>
        </li>
      ))}
    </ol>
  );
}

const changeTone = {
  added: {
    panel: "bg-success",
    badge: "bg-success-foreground/10 text-success-foreground",
    text: "text-foreground",
    label: "administration.situationCard.changeAdded",
    variant: "success",
  },
  rewritten: {
    panel: "bg-warning",
    badge: "bg-warning-foreground/10 text-warning-foreground",
    text: "text-foreground",
    label: "administration.situationCard.changeRewritten",
    variant: "warning",
  },
  removed: {
    panel: "bg-destructive",
    badge: "bg-destructive-foreground/10 text-destructive-foreground",
    text: "text-foreground line-through",
    label: "administration.situationCard.changeRemoved",
    variant: "destructive",
  },
} as const;

function DiffText({ before, after, references }: { before: string; after: string; references: readonly number[] }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 space-y-3">
      {visibleChanges(before, after).map((part, index) =>
        part.kind === "same" ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-secondary-text" key={index}>
            {part.value}
            <SourceReferences numbers={references} />
          </p>
        ) : (
          <ChangeBlock key={index} part={part} references={references} label={t(changeTone[part.kind].label)} />
        ),
      )}
    </div>
  );
}

function ChangeBlock({
  part,
  references,
  label,
}: {
  part: VisibleChange;
  references: readonly number[];
  label: string;
}) {
  if (part.kind === "same") return null;
  const tone = changeTone[part.kind];
  return (
    <div className={`rounded-lg px-3 py-3 ${tone.panel}`}>
      <Badge className={tone.badge} variant={tone.variant}>
        {label}
      </Badge>
      <p className={`mt-2 text-sm leading-relaxed whitespace-pre-wrap ${tone.text}`}>
        {part.value}
        {part.kind === "removed" ? null : <SourceReferences numbers={references} />}
      </p>
    </div>
  );
}

function LoadingLines() {
  return (
    <div aria-hidden="true" className="space-y-2">
      <Skeleton className="h-3 w-2/3 rounded-full" />
      <Skeleton className="h-3 w-1/2 rounded-full" />
    </div>
  );
}

function StreamingText({ text, onDone }: { text: string; onDone?: () => void }) {
  const [count, setCount] = useState(0);
  const reported = useRef(false);
  useEffect(() => {
    const step = Math.max(12, Math.ceil(text.length / 24));
    const timer = window.setInterval(() => {
      setCount((current) => {
        const next = Math.min(text.length, current + step);
        if (next >= text.length) window.clearInterval(timer);
        return next;
      });
    }, 40);
    return () => window.clearInterval(timer);
  }, [text]);
  useEffect(() => {
    if (count >= text.length && !reported.current) {
      reported.current = true;
      onDone?.();
    }
  }, [count, onDone, text.length]);
  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap text-secondary-text">
      {text.slice(0, count)}
      {count < text.length ? (
        <span aria-hidden="true" className="ml-0.5 inline-block h-4 w-px animate-pulse bg-foreground align-middle" />
      ) : null}
    </p>
  );
}
